// ============================================================
// TMR Portal Admin — modal de confirmação reutilizável
// ============================================================
import { useState } from 'react';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { Btn } from './primitives';
import { Icons, StrokeIcon } from '../lib/icons';
import { OverlayPortal } from './OverlayPortal';

export function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  danger = false,
  icon,
  onConfirm,
  onClose,
}: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  icon?: ReactNode;
  /**
   * The action. When it returns a promise the dialog stays open, disabled, until it settles — closing
   * first would put the error toast behind a screen the operator already believes they left, and would
   * let a double click send the request twice.
   */
  onConfirm: () => void | Promise<unknown>;
  onClose: () => void;
}) {
  const [pending, setPending] = useState(false);
  const stop = (e: MouseEvent) => e.stopPropagation();

  const confirm = async () => {
    if (pending) return;
    setPending(true);
    try {
      await onConfirm();
      onClose();
    } finally {
      setPending(false);
    }
  };
  const confirmStyle: CSSProperties | undefined = danger ? { background: 'var(--danger)', color: 'white' } : undefined;
  return (
    <OverlayPortal>
      <div onClick={pending ? undefined : onClose} style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div onClick={stop} style={{ width: 420, maxWidth: '100%', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>
          <div style={{ padding: '24px 24px 8px', display: 'flex', gap: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: danger ? 'oklch(94% 0.04 28)' : 'var(--sage-100)', color: danger ? 'var(--danger)' : 'var(--forest-800)' }}>
              <StrokeIcon d={icon || Icons.alert} size={18} strokeWidth={2} />
            </div>
            <div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 17, margin: 0, letterSpacing: '-0.01em' }}>{title}</h3>
              <div style={{ fontSize: 13.5, color: 'var(--fg-muted)', marginTop: 6, lineHeight: 1.5 }}>{message}</div>
            </div>
          </div>
          <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Btn variant="secondary" onClick={onClose} disabled={pending}>
              {cancelLabel}
            </Btn>
            <Btn
              variant="primary"
              icon={Icons.check}
              style={confirmStyle}
              disabled={pending}
              onClick={() => void confirm()}
            >
              {pending ? 'Aguarde…' : confirmLabel}
            </Btn>
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}
